/**
 * AuthContext — wraps Firebase Auth state for the whole app.
 *
 * Exposes:
 *   user         — Firebase User object (null when signed out / loading)
 *   loading      — true during the initial auth-state resolution
 *   idToken      — current ID token string (refreshed automatically)
 *   signOut      — convenience wrapper
 *   refreshToken — force-refresh the ID token (call after profile changes)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getIdTokenResult,
  reload,
} from 'firebase/auth';
import { firebaseAuth } from '../firebase';
import { setAuthTokenGetter } from '../api';

// Wire up the token getter once at module load so all API calls carry the
// current Firebase ID token without needing to pass it around explicitly.
setAuthTokenGetter(() =>
  firebaseAuth.currentUser
    ? firebaseAuth.currentUser.getIdToken()
    : Promise.resolve(null)
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [claims, setClaims]   = useState({});  // decoded custom claims
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      setUser(fbUser);
      if (fbUser) {
        try {
          const result = await getIdTokenResult(fbUser);
          setIdToken(result.token);
          setClaims(result.claims || {});
        } catch {
          setIdToken(null);
          setClaims({});
        }
      } else {
        setIdToken(null);
        setClaims({});
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(firebaseAuth);
  }, []);

  const refreshToken = useCallback(async () => {
    if (!firebaseAuth.currentUser) return null;
    const token = await firebaseAuth.currentUser.getIdToken(/* forceRefresh */ true);
    setIdToken(token);
    return token;
  }, []);

  // Reload the Firebase user object and push the updated state into React.
  // reload() refreshes emailVerified in-place but does NOT fire onAuthStateChanged,
  // so we must explicitly update state here for the app to re-render.
  const reloadUser = useCallback(async () => {
    const fbUser = firebaseAuth.currentUser;
    if (!fbUser) return;
    await reload(fbUser);
    try {
      const result = await getIdTokenResult(fbUser);
      setIdToken(result.token);
      setClaims(result.claims || {});
    } catch {
      setIdToken(null);
      setClaims({});
    }
    setUser({ ...fbUser }); // spread to a new object so React detects the change
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, idToken, claims, signOut, refreshToken, reloadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
